import React, { useEffect, useState, useRef } from 'react';
import { Typography, Progress } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';

const { Title, Paragraph } = Typography;

interface Props {
  isProcessing: boolean;
  title?: string;
  description?: string;
}

const Processing: React.FC<Props> = ({
  isProcessing,
  title = 'Processing Documents',
  description = 'Extracting data from uploaded files...',
}) => {
  const { services } = useSelector((state: RootState) => state.analysis);
  const actualProgress = services.progress.progress
  const progressMessage = services.progress.progressMessage

  const [visualProgress, setVisualProgress] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear interval on cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isProcessing) {
      setVisualProgress(10);
      setStartTime(null);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return;
    }

    if (actualProgress >= 100) {
      setVisualProgress(100);
      setStartTime(null);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return;
    }

    if (actualProgress < 50) {
      setVisualProgress(actualProgress);
      setStartTime(null);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return;
    }

    // Phase >= 50%
    if (startTime === null) {
      setStartTime(Date.now());
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      console.log('Interval running');
      setVisualProgress(prev => {
        if (actualProgress >= 100) return 100;
        if (prev >= 99) return prev;

        const elapsedSeconds = (Date.now() - (startTime ?? Date.now())) / 1000;
        const maxTime = 60;
        const timeProgress = Math.min(elapsedSeconds / maxTime, 1);
        const calculated = 50 + 49 * (1 - Math.exp(-3 * timeProgress));

        return Math.min(calculated, 100);
      });
    }, 1000);
  }, [isProcessing, actualProgress, startTime]);

  const displayProgress = Math.round(visualProgress);
  const statusMessage =
    actualProgress >= 50
      ? 'AI analyzing content... '
      : progressMessage || 'Preparing files...';

  return (
    <>
      <Title level={4} className="panel-title">{title}</Title>
      <Paragraph style={{ color: "#6b7280"}}>{description}</Paragraph>

      {isProcessing && (
        <div className="loading-indicator" style={{ margin: '30px 0' }}>
          <div className="progress-container" style={{ width: '100%' }}>
            <div className="progress-label" style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '10px'
            }}>
              {isProcessing && visualProgress < 100 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: '#6b7280'
                }}>
                  <div className="loading-spinner"
                    style={{
                      marginRight: '12px',
                      width: '24px',
                      height: '24px',
                      border: '2px solid #e5e7eb',
                      borderTopColor: '#4361ee',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}
                  />
                </div>
              )}
              <span className="progress-text" style={{ fontWeight: 500 }}>
                {statusMessage}
              </span>
            </div>
            <Progress
              percent={displayProgress}
              status="active"
              strokeColor={{ '0%': '#4361ee', '100%': '#4cc9f0' }}
              strokeWidth={8}
              format={() => ''}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Processing;